import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import {
  GrooveActionDirective,
  GrooveSpotlightDirective,
  GrooveSurfaceDirective,
} from '@groove/angular';
import { DATA_CONFIG } from '../../injection-tokens';
import { RouteUiDataService } from '../../route-ui-data';
import type { CtaLocation } from './page-cta.types';

const LOCKED_BODY =
  'Online bass lessons via Zoom for students of any level. Reach out to talk through your goals.';

const DEFAULT_HEADING = 'Questions about lessons?';

/**
 * The shared "Contact Russ" CTA card.
 *
 * The `bottom`-location instance reads its `heading` and `body` from
 * `RouteUiDataService.bottomCta()` (sourced from each route's
 * `data.bottomCta` in `app.routes.ts`). The `aside`-location instance
 * keeps the inputs API — it's demoed in the /groove styleguide and is
 * unit-tested directly with input bindings.
 *
 * Inputs are preserved on the `bottom` location as well so the component
 * is independently testable. When a `bottom`-location caller passes no
 * `heading` / `body` (the default in route templates), values come from
 * route data. When they ARE passed explicitly, the inputs win — this
 * preserves the existing test harness without an asymmetric API.
 */
@Component({
  selector: 'app-page-cta',
  imports: [
    GrooveActionDirective,
    GrooveSpotlightDirective,
    GrooveSurfaceDirective,
  ],
  templateUrl: './page-cta.component.html',
  styleUrl: './page-cta.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-location]': 'location()',
  },
})
export class PageCtaComponent {
  // Where this CTA is rendered. Styling + which optional fields are
  // shown depends on the location.
  readonly location = input.required<CtaLocation>();

  // Per-call heading override, usable by either location (e.g. in unit
  // tests, or the /groove styleguide demo). When omitted on the bottom
  // location, falls back to the route's `bottomCta.heading`; the aside
  // location falls back to the default prompt.
  readonly heading = input<string | undefined>(undefined);

  // Per-call body override, usable by either location (e.g. in unit
  // tests, or the /groove styleguide demo). When omitted on the bottom
  // location, falls back to the route's `bottomCta.body` if defined,
  // otherwise the locked body; the aside location falls back to the
  // locked body.
  readonly body = input<string | undefined>(undefined);

  private readonly routeUiData = inject(RouteUiDataService);

  // Resolved heading. Bottom location falls back to route data; aside
  // location falls back to the default prompt.
  protected readonly resolvedHeading = computed<string>(() => {
    const fromInput = this.heading();
    if (fromInput !== undefined) return fromInput;
    if (this.location() === 'bottom') {
      return this.routeUiData.bottomCta()?.heading ?? DEFAULT_HEADING;
    }
    return DEFAULT_HEADING;
  });

  // Resolved body. Bottom location falls back to route data's body
  // override (used by /about/performance, /x-ploration-jazz) before
  // the locked default; aside uses the locked default.
  protected readonly resolvedBody = computed<string>(() => {
    const fromInput = this.body();
    if (fromInput !== undefined) return fromInput;
    if (this.location() === 'bottom') {
      return this.routeUiData.bottomCta()?.body ?? LOCKED_BODY;
    }
    return LOCKED_BODY;
  });

  // Phone / email hrefs come from DATA_CONFIG so the header + footer +
  // CTAs share a single source of truth (pattern established in the
  // header slice).
  private readonly dataConfig = inject(DATA_CONFIG);
  protected readonly phoneHref = this.dataConfig.phoneHref;
  protected readonly emailHref = this.dataConfig.emailHref;

  // All CTA locations use the shorter "Call Russ" label. The phone
  // number is already in the hero text links across every viewport,
  // so the bottom CTA doesn't need to repeat it.
  protected readonly callLabel = 'Call Russ';
}
